import { useState } from 'react';
import { useTranslate } from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Skeleton,
  Alert,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import LinkIcon from '@mui/icons-material/Link';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import TelegramIcon from '@mui/icons-material/Telegram';
import GroupsIcon from '@mui/icons-material/Groups';
import { useTelegramBot } from '../../hooks/useTelegramBot';
import { ConfirmationDialog } from '../../resources/tickets/components/dialogs/ConfirmationDialog';
import { ChannelConnectionSettingsDialog } from './ChannelConnectionSettingsDialog';

export interface TelegramBotManagerProps {
  channelConnectionId: number;
  channelConnectionName?: string;
  onStatusChange?: (isConnected: boolean) => void;
}

export function TelegramBotManager({
  channelConnectionId,
  channelConnectionName,
  onStatusChange,
}: TelegramBotManagerProps) {
  const translate = useTranslate();
  const {
    connection,
    loading,
    actionLoading,
    isConnected,
    botUsername,
    botFirstName,
    botStatus,
    botStatusLoading,
    refetch,
    refetchBotStatus,
    setupWebhook,
    disconnect,
  } = useTelegramBot({
    channelConnectionId,
    onStatusChange,
  });

  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);

  const handleDisconnect = async () => {
    await disconnect();
    setConfirmDisconnect(false);
  };

  const handleRefresh = () => {
    refetch();
    refetchBotStatus();
  };

  const displayBotName = botStatus?.botUsername
    ? `@${botStatus.botUsername}`
    : botUsername
      ? (botUsername.startsWith('@') ? botUsername : `@${botUsername}`)
      : null;

  const displayBotFullName = botStatus
    ? `${botStatus.botFirstName}${displayBotName ? ` (${displayBotName})` : ''}`
    : displayBotName;

  // Determine group config state from live status
  const canJoinGroups = botStatus?.canJoinGroups ?? null;
  const canReadAllGroupMessages = botStatus?.canReadAllGroupMessages ?? null;
  const groupFullyConfigured = canJoinGroups === true && canReadAllGroupMessages === true;

  // Only show webhook error if it's recent (last 5 min) and there are pending updates
  const hasRecentWebhookError = (() => {
    if (!botStatus?.lastWebhookError || !botStatus.lastWebhookErrorDate) return false;
    const fiveMinutesAgo = Math.floor(Date.now() / 1000) - 300;
    return botStatus.lastWebhookErrorDate > fiveMinutesAgo && (botStatus.pendingUpdateCount ?? 0) > 0;
  })();

  if (loading && !connection) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Skeleton variant="circular" width={40} height={40} />
            <Box flex={1}>
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="40%" />
            </Box>
          </Box>
          <Skeleton variant="rectangular" height={100} />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card variant="outlined">
        <CardContent>
          {/* Header */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <TelegramIcon color="info" />
              <Typography variant="h6">
                {channelConnectionName || translate('resources.channel.telegram.title')}
              </Typography>
            </Box>
            <Box display="flex" gap={0.5}>
              <Tooltip title={translate('resources.channel.settings.title')}>
                <span>
                  <IconButton onClick={() => setSettingsDialogOpen(true)} size="small">
                    <SettingsIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={translate('ra.action.refresh')}>
                <span>
                  <IconButton onClick={handleRefresh} disabled={loading || botStatusLoading} size="small">
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Status */}
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            {isConnected ? (
              <CheckCircleIcon color="success" />
            ) : (
              <ErrorIcon color="error" />
            )}
            <Box flex={1}>
              <Typography variant="body2" color="text.secondary">
                {translate('resources.channel.telegram.status')}
              </Typography>
              <Chip
                label={translate(
                  `resources.channel.telegram.statuses.${isConnected ? 'connected' : 'disconnected'}`
                )}
                color={isConnected ? 'success' : 'error'}
                size="small"
              />
            </Box>
          </Box>

          {/* Bot Info */}
          {isConnected && (
            <Box mb={2}>
              {botStatusLoading && !botStatus ? (
                <Skeleton variant="text" width="50%" />
              ) : botStatus ? (
                <Box display="flex" flexDirection="column" gap={0.5}>
                  <Typography variant="body2">
                    <strong>{translate('resources.channel.telegram.botInfo.name')}:</strong>{' '}
                    {botStatus.botFirstName}
                  </Typography>
                  {botStatus.botUsername && (
                    <Typography variant="body2">
                      <strong>{translate('resources.channel.telegram.botInfo.username')}:</strong>{' '}
                      @{botStatus.botUsername}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    <strong>ID:</strong> {botStatus.botId}
                  </Typography>
                  <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                    <Chip
                      label={translate('resources.channel.telegram.botInfo.canJoinGroups')}
                      color={botStatus.canJoinGroups ? 'success' : 'default'}
                      variant={botStatus.canJoinGroups ? 'filled' : 'outlined'}
                      size="small"
                    />
                    <Chip
                      label={translate('resources.channel.telegram.botInfo.canReadGroupMessages')}
                      color={botStatus.canReadAllGroupMessages ? 'success' : 'default'}
                      variant={botStatus.canReadAllGroupMessages ? 'filled' : 'outlined'}
                      size="small"
                    />
                  </Box>
                </Box>
              ) : (botFirstName || botUsername || connection?.externalId) ? (
                <Box display="flex" flexDirection="column" gap={0.5}>
                  {botFirstName && (
                    <Typography variant="body2">
                      <strong>{translate('resources.channel.telegram.botInfo.name')}:</strong>{' '}
                      {botFirstName}
                    </Typography>
                  )}
                  {botUsername && (
                    <Typography variant="body2">
                      <strong>{translate('resources.channel.telegram.botInfo.username')}:</strong>{' '}
                      {botUsername}
                    </Typography>
                  )}
                  {!botFirstName && !botUsername && connection?.externalId && (
                    <Typography variant="body2">
                      <strong>ID:</strong> {connection.externalId}
                    </Typography>
                  )}
                </Box>
              ) : null}
            </Box>
          )}

          {/* Not Connected Info */}
          {!isConnected && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {translate('resources.channel.telegram.setupInstructions')}
            </Alert>
          )}

          {/* Group Setup Instructions — only show when not fully configured */}
          {isConnected && !groupFullyConfigured && (
            <Alert severity="warning" icon={<GroupsIcon />} sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                {translate('resources.channel.telegram.groupSetup.title')}
              </Typography>
              <Typography variant="body2">
                {translate('resources.channel.telegram.groupSetup.description')}
              </Typography>

              {/* Step 1: Allow Groups — only show if not enabled */}
              {canJoinGroups !== true && (
                <>
                  <Typography variant="body2" fontWeight="bold" sx={{ mt: 1.5 }}>
                    {translate('resources.channel.telegram.groupSetup.allowGroupsTitle')}
                  </Typography>
                  <Box component="ol" sx={{ pl: 2, mt: 0.5, mb: 0, '& li': { mb: 0.5 } }}>
                    <li>
                      <Typography variant="body2">
                        {translate('resources.channel.telegram.groupSetup.allowGroups.step1')}
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        {translate('resources.channel.telegram.groupSetup.allowGroups.step2', {
                          botName: displayBotName || 'seu bot',
                        })}
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        {translate('resources.channel.telegram.groupSetup.allowGroups.step3')}
                      </Typography>
                    </li>
                  </Box>
                </>
              )}

              {/* Step 2: Disable Privacy Mode — only show if not enabled */}
              {canReadAllGroupMessages !== true && (
                <>
                  <Typography variant="body2" fontWeight="bold" sx={{ mt: 1.5 }}>
                    {translate('resources.channel.telegram.groupSetup.privacyTitle')}
                  </Typography>
                  <Box component="ol" sx={{ pl: 2, mt: 0.5, mb: 0, '& li': { mb: 0.5 } }}>
                    <li>
                      <Typography variant="body2">
                        {translate('resources.channel.telegram.groupSetup.privacy.step1')}
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        {translate('resources.channel.telegram.groupSetup.privacy.step2', {
                          botName: displayBotName || 'seu bot',
                        })}
                      </Typography>
                    </li>
                  </Box>
                </>
              )}

              {/* Final step */}
              <Typography variant="body2" sx={{ mt: 1.5, fontStyle: 'italic' }}>
                {translate('resources.channel.telegram.groupSetup.readdBot')}
              </Typography>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {translate('resources.channel.telegram.groupSetup.note')}
              </Typography>
            </Alert>
          )}

          {/* Group fully configured */}
          {isConnected && groupFullyConfigured && (
            <Alert severity="success" icon={<GroupsIcon />} sx={{ mb: 2 }}>
              <Typography variant="body2">
                {translate('resources.channel.telegram.groupSetup.allConfigured')}
              </Typography>
            </Alert>
          )}

          {/* Webhook error — only show if recent and updates are stuck */}
          {isConnected && hasRecentWebhookError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                {translate('resources.channel.telegram.botInfo.webhookError')}
              </Typography>
              <Typography variant="body2">
                {translate('resources.channel.telegram.botInfo.webhookErrorDescription', {
                  error: botStatus.lastWebhookError,
                })}
              </Typography>
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Actions */}
          <Box display="flex" gap={1} flexWrap="wrap">
            {!isConnected && (
              <Button
                variant="contained"
                size="small"
                color="primary"
                startIcon={actionLoading === 'setup' ? <CircularProgress size={16} /> : <LinkIcon />}
                onClick={setupWebhook}
                disabled={!!actionLoading}
              >
                {translate('resources.channel.telegram.connect')}
              </Button>
            )}
            {isConnected && (
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={
                  actionLoading === 'disconnect' ? <CircularProgress size={16} /> : <PowerSettingsNewIcon />
                }
                onClick={() => setConfirmDisconnect(true)}
                disabled={!!actionLoading}
              >
                {translate('resources.channel.telegram.disconnect')}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Confirm Disconnect Dialog */}
      <ConfirmationDialog
        open={confirmDisconnect}
        onClose={() => setConfirmDisconnect(false)}
        onConfirm={handleDisconnect}
        title={translate('resources.channel.telegram.confirmDisconnect')}
        message={translate('resources.channel.telegram.disconnectWarning')}
        confirmText={translate('resources.channel.telegram.disconnect')}
        isDangerous
        isLoading={actionLoading === 'disconnect'}
      />

      {/* Settings Dialog */}
      <ChannelConnectionSettingsDialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        channelConnectionId={channelConnectionId}
        channelType="telegram"
      />
    </>
  );
}

export default TelegramBotManager;
